import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Target, Eye, Award, Users } from 'lucide-react';
import axios from 'axios';
import Reveal from '../components/Reveal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const About = () => {
  const [companyInfo, setCompanyInfo] = useState({
    company_name: 'International Quality Group',
    about_description: "With over a decade of experience in international logistics, we've built our reputation on trust, reliability, and innovation. We're more than a shipping company – we're your global logistics partner.",
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(`${API}/content/company-info`);
        setCompanyInfo(response.data);
      } catch (error) {
        console.error('Failed to fetch company info:', error);
      }
    };
    fetchData();
  }, []);
  const values = [
    {
      icon: Target,
      title: 'Our Mission',
      description: 'To provide world-class logistics solutions that empower businesses to reach global markets efficiently and reliably.',
    },
    {
      icon: Eye,
      title: 'Our Vision',
      description: 'To be the most trusted logistics partner worldwide, known for innovation, reliability, and exceptional customer service.',
    },
    {
      icon: Award,
      title: 'Excellence',
      description: 'We maintain the highest standards in every shipment, ensuring quality and reliability at every step.',
    },
    {
      icon: Users,
      title: 'Customer Focus',
      description: 'Your success is our priority. We tailor our services to meet your unique logistics needs.',
    },
  ];

  const milestones = [
    { year: '2010', title: 'Company Founded', description: 'Started with a vision to revolutionize logistics' },
    { year: '2015', title: 'Global Expansion', description: 'Extended services to 25+ countries' },
    { year: '2020', title: 'Technology Integration', description: 'Launched advanced tracking systems' },
    { year: '2024', title: 'Industry Leader', description: 'Serving 50+ countries with 10K+ monthly shipments' },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-emerald-50 via-white to-gray-50 py-20">
        <div className="pointer-events-none absolute -top-24 -right-16 w-96 h-96 bg-emerald-200/40 rounded-full blur-3xl animate-blob" aria-hidden="true" />
        <div className="pointer-events-none absolute -bottom-24 -left-16 w-80 h-80 bg-red-200/25 rounded-full blur-3xl animate-blob" style={{ animationDelay: '4s' }} aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center max-w-3xl mx-auto">
            <h1 className="text-5xl font-bold text-gray-900 mb-6">
              About <span className="gradient-text">International Quality Group</span>
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed">
              {companyInfo.about_description}
            </p>
          </Reveal>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <Reveal>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">Our Story</h2>
              <div className="space-y-4 text-gray-600 text-lg leading-relaxed">
                <p>
                  Founded in 2010, International Quality Group began with a simple mission: to make international shipping accessible, reliable, and efficient for businesses of all sizes.
                </p>
                <p>
                  Today, we've grown into a trusted logistics partner serving over 50 countries, handling thousands of shipments monthly, and maintaining an industry-leading 99.8% on-time delivery rate.
                </p>
                <p>
                  Our success is built on our commitment to innovation, investing in cutting-edge technology and building strong partnerships with carriers worldwide to ensure your cargo reaches its destination safely and on time.
                </p>
              </div>
            </Reveal>
            <Reveal delay={120} className="relative overflow-hidden rounded-2xl shadow-2xl">
              <img
                src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=800&q=80"
                alt="Team"
                className="w-full transition-transform duration-500 hover:scale-105"
              />
            </Reveal>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Our Values & Principles
            </h2>
            <p className="text-xl text-gray-600">
              The foundation of everything we do
            </p>
          </Reveal>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <Reveal key={index} delay={index * 100}>
                  <Card className="h-full border-2 hover:border-emerald-500 hover:shadow-xl hover:-translate-y-1.5 transition-all duration-300 group">
                    <CardContent className="p-6 space-y-4">
                      <div className="w-14 h-14 bg-emerald-100 rounded-xl flex items-center justify-center transition-colors group-hover:bg-emerald-500">
                        <Icon className="text-emerald-600 transition-colors group-hover:text-white" size={28} />
                      </div>
                      <h3 className="text-xl font-semibold text-gray-900">{value.title}</h3>
                      <p className="text-gray-600">{value.description}</p>
                    </CardContent>
                  </Card>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Our Journey
            </h2>
            <p className="text-xl text-gray-600">
              Key milestones that shaped our growth
            </p>
          </Reveal>
          <div className="relative">
            <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-emerald-200 hidden md:block"></div>
            <div className="space-y-12">
              {milestones.map((milestone, index) => (
                <Reveal key={index} delay={index * 80} className={`relative flex items-center ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                  <div className="flex-1">
                    <Card className={`${index % 2 === 0 ? 'md:mr-8' : 'md:ml-8'} border-2 hover:border-emerald-500 hover:shadow-xl hover:-translate-y-1 transition-all duration-300`}>
                      <CardContent className="p-6">
                        <div className="text-3xl font-bold text-emerald-600 mb-2">{milestone.year}</div>
                        <h3 className="text-xl font-semibold text-gray-900 mb-2">{milestone.title}</h3>
                        <p className="text-gray-600">{milestone.description}</p>
                      </CardContent>
                    </Card>
                  </div>
                  <div className="absolute left-1/2 transform -translate-x-1/2 w-4 h-4 bg-emerald-600 rounded-full border-4 border-white hidden md:block"></div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;